import { openModal } from '@kcd/app-interface/dist/web2app';
import { trackEvent } from '@kcd/event-tracker';

import type { OAISchema } from '~/types/openapi';
import { isMoreThan } from '~/utils/number';

import { useMembershipStatus } from '../../hooks/useMembershipStatus';

import {
  BonuspackCloverFill18x18Icon,
  CheveronRightIcon24x24,
  MembershipStandard28x14Icon,
} from '~/assets/icons';

type Props = {
  isSomeChecked: boolean;
  discountedTotalAmount: OAISchema['MarketInShoppingCartPaymentResponse']['discountedTotalAmount'];
  membershipRegisteredExpectedAmount?: number;
};

export function CartReceiptMembershipNudge({
  isSomeChecked,
  discountedTotalAmount,
  membershipRegisteredExpectedAmount,
}: Props) {
  const {
    isTrialAvailable,
    isAvailableToSubscribe,
    isAvailableToSubscribeBonusPack,
    getRegisterMembershipUrl,
  } = useMembershipStatus();

  const nudgeType = (() => {
    if (isTrialAvailable) {
      return 'plus_membership_free';
    }

    if (isAvailableToSubscribe) {
      return 'plus_membership';
    }

    if (isAvailableToSubscribeBonusPack) {
      return 'lucky_bonus_pack';
    }

    return '';
  })();

  const onClickRegisterMembership = () => {
    const registerUrl = getRegisterMembershipUrl();

    trackEvent('clicked button in 마켓/장바구니/멤버십넛지', {
      clicked_area: 'nudge_below_price',
      membership_nudge_type: nudgeType,
    });

    if (registerUrl) {
      openModal({
        url: registerUrl,
      });
    }
  };

  const NudgeContent = (() => {
    const isMoreThanDiscountedTotalAmount = isMoreThan(
      discountedTotalAmount,
      membershipRegisteredExpectedAmount,
    );

    if (isSomeChecked) {
      // 일반 쿠폰 할인가가 플멤 쿠폰 할인가보다 더 높다면
      if (isMoreThanDiscountedTotalAmount) {
        if (isTrialAvailable) {
          return (
            <div className="flex justify-between">
              <p className="flex items-center gap-4 text-paragraph typo-paragraph300-regular">
                <MembershipStandard28x14Icon />
                멤버십 쿠폰 적용 시
              </p>
              <p className="flex items-center gap-4 text-accent typo-paragraph300-semibold">
                {membershipRegisteredExpectedAmount?.toLocaleString()}원
                <CheveronRightIcon24x24 className="[&_path]:fill-blue-600" />
              </p>
            </div>
          );
        }

        if (isAvailableToSubscribe) {
          return (
            <div className="flex justify-between">
              <p className="flex items-center gap-4 text-paragraph typo-paragraph300-regular">
                <MembershipStandard28x14Icon />
                멤버십 쿠폰 적용 시
              </p>
              <p className="flex items-center gap-4 text-accent typo-paragraph300-semibold">
                {membershipRegisteredExpectedAmount?.toLocaleString()}원
                <CheveronRightIcon24x24 className="[&_path]:fill-blue-600" />
              </p>
            </div>
          );
        }

        if (isAvailableToSubscribeBonusPack) {
          return (
            <div className="flex justify-between">
              <p className="flex items-center gap-4 text-paragraph typo-paragraph300-regular">
                <BonuspackCloverFill18x18Icon />
                행운 보너스팩 적용 시
              </p>
              <p className="flex items-center gap-4 text-accent typo-paragraph300-semibold">
                {membershipRegisteredExpectedAmount?.toLocaleString()}원
                <CheveronRightIcon24x24 className="[&_path]:fill-blue-600" />
              </p>
            </div>
          );
        }
      } else {
        // 멤버십 할인가보다 다른 쿠폰 스킴이 더 큰경우 또는 같을떄
        if (isTrialAvailable) {
          return (
            <div className="flex justify-between">
              <p className="flex items-center gap-4 text-paragraph typo-paragraph300-regular">
                <MembershipStandard28x14Icon />총 6만원 쿠폰팩
              </p>
              <p className="flex items-center gap-4 text-accent typo-paragraph300-semibold">
                첫 4주 무료체험
                <CheveronRightIcon24x24 className="[&_path]:fill-blue-600" />
              </p>
            </div>
          );
        }

        if (isAvailableToSubscribe) {
          return (
            <div className="flex justify-between">
              <p className="flex items-center gap-4 text-paragraph typo-paragraph300-regular">
                <MembershipStandard28x14Icon />총 6만원 쿠폰팩
              </p>
              <p className="flex items-center gap-4 text-accent typo-paragraph300-semibold">
                보러가기
                <CheveronRightIcon24x24 className="[&_path]:fill-blue-600" />
              </p>
            </div>
          );
        }

        if (isAvailableToSubscribeBonusPack) {
          return (
            <div className="flex justify-between">
              <p className="flex items-center gap-4 text-paragraph typo-paragraph300-regular">
                <BonuspackCloverFill18x18Icon />총 2만원 쿠폰팩에 보너스할인
              </p>
              <p className="flex items-center gap-4 text-accent typo-paragraph300-semibold">
                보러가기
                <CheveronRightIcon24x24 className="[&_path]:fill-blue-600" />
              </p>
            </div>
          );
        }
      }
    } else {
      // 상품을 선택 안했을 떄
      if (isTrialAvailable) {
        return (
          <div className="flex justify-between">
            <p className="flex items-center gap-4 text-paragraph typo-paragraph300-regular">
              <MembershipStandard28x14Icon />
              멤버십 쿠폰 적용 시
            </p>
            <p className="flex items-center gap-4 text-accent typo-paragraph300-semibold">
              0원
              <CheveronRightIcon24x24 className="[&_path]:fill-blue-600" />
            </p>
          </div>
        );
      }

      if (isAvailableToSubscribe) {
        return (
          <div className="flex justify-between">
            <p className="flex items-center gap-4 text-paragraph typo-paragraph300-regular">
              <MembershipStandard28x14Icon />
              멤버십 쿠폰 적용 시
            </p>
            <p className="flex items-center gap-4 text-accent typo-paragraph300-semibold">
              0원
              <CheveronRightIcon24x24 className="[&_path]:fill-blue-600" />
            </p>
          </div>
        );
      }

      if (isAvailableToSubscribeBonusPack) {
        return (
          <div className="flex justify-between">
            <p className="flex items-center gap-4 text-paragraph typo-paragraph300-regular">
              <BonuspackCloverFill18x18Icon />
              행운 보너스팩 적용 시
            </p>
            <p className="flex items-center gap-4 text-accent typo-paragraph300-semibold">
              0원
              <CheveronRightIcon24x24 className="[&_path]:fill-blue-600" />
            </p>
          </div>
        );
      }
    }

    return <></>;
  })();

  if (!NudgeContent) return <></>;

  return (
    <div
      className="py-8"
      role="presentation"
      onClick={onClickRegisterMembership}
    >
      {NudgeContent}
    </div>
  );
}
